// Copyright 2019 Sorama B.V.

export class Matrix {
    /**
     * Creates an identity matrix of the provided dimension.
     * @param dim Dimension of the identity matrix.
     */
    public static identity(dim = 4): number[][] {
        const matrix = <number[][]>new Array(dim);
        for (let i = 0; i < dim; i++) {
            matrix[i] = <number[]>new Array(dim);
            for (let j = 0; j < dim; j++) {
                matrix[i][j] = i == j ? 1 : 0;
            }
        }

        return matrix;
    }

    /**
     * Creates a scaling matrix with the provided scale coefficients.
     * @param scales Matrix of scaling coefficients, one for each dimension. A scales array of length 4 will result in a matrix of 4x4.
     */
    public static scale(...scales: number[]): number[][] {
        const scaleMatrix = new Array<number[]>(scales.length);
        for (let i = 0; i < scales.length; i++) {
            scaleMatrix[i] = new Array<number>(scales.length);
            for (let j = 0; j < scales.length; j++) {
                scaleMatrix[i][j] = i == j ? scales[i] : 0;
            }
        }

        return scaleMatrix;
    }

    /**
     * Creates a translate matrix with the provided offsets.
     * @param offsets Matrix of offsets, one for each dimension. An offsets array of length 3 will result in a matrix of 4x4.
     */
    public static translate(...offsets: number[]): number[][] {
        const transMatrix = new Array<number[]>(offsets.length + 1);
        for (let i = 0; i < offsets.length; i++) {
            transMatrix[i] = new Array<number>(offsets.length + 1);
            for (let j = 0; j < offsets.length; j++) {
                transMatrix[i][j] = i == j ? 1 : 0;
            }
            transMatrix[i][offsets.length] = offsets[i];
        }

        transMatrix[offsets.length] = new Array<number>(offsets.length + 1);
        for (let j = 0; j < offsets.length; j++) {
            transMatrix[offsets.length][j] = 0;
        }
        transMatrix[offsets.length][offsets.length] = 1;

        return transMatrix;
    }

    /**
     * Multiplies 2 matrices. Note that the inner dimensions of the provided matrices must match.
     * @param lhs Left hand side of the matrix multiplication.
     * @param rhs Right hand side of the matrix multiplication.
     */
    public static multiply(lhs: number[][], rhs: number[][]): number[][] {
        const lhsd0 = lhs.length;
        const rhsd0 = rhs.length;

        if (lhsd0 === 0 || rhsd0 === 0) return [];

        const lhsd1 = lhs[0].length;
        const rhsd1 = rhs[0].length;

        if (lhsd1 !== rhsd0) {
            throw new Error("Multiply: Matrix dimensions do not match");
        }

        const out = new Array<number[]>(lhsd0);
        for (let i = 0; i < lhsd0; i++) {
            out[i] = new Array<number>(rhsd1);
            for (let j = 0; j < rhsd1; j++) {
                let val = 0.0;
                for (let k = 0; k < lhsd1; k++) {
                    val += lhs[i][k] * rhs[k][j];
                }

                out[i][j] = val;
            }
        }

        return out;
    }

    /**
     * Converts the provided matrix to a float 32 single dimensional array.
     * @param matrix Matrix to convert.
     */
    public static toFloat32Array(matrix: number[][]): Float32Array {
        if (matrix.length === 0) {
            return new Float32Array(0);
        }

        const dim1 = matrix[0].length;
        if (dim1 === 0) {
            return new Float32Array(0);
        }

        const elements = matrix.length * dim1;
        const m = new Float32Array(elements);

        //let idx = 0;
        for (let i = 0; i < matrix.length; i++) {
            if (matrix[i].length != dim1) {
                throw new Error("Matrix is not rectangular");
            }

            for (let j = 0; j < dim1; j++) {
                const idx = j * matrix.length + i;
                m[idx] = matrix[i][j];
            }
        }

        return m;
    }
}
